import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCalendarXmark,
  faCircleCheck,
  faCircleXmark,
  faClock,
  faThumbsDown,
  faThumbsUp,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import type { AppointmentAttendance } from '@/types/appointments';
import Avatar from '@/ui/components/Avatar';
import Heading from '@/ui/components/Heading';
import MetricCard from '@/ui/components/MetricCard';
import PeopleAvatarList from '@/ui/components/PeopleAvatarList';
import formatPersonName from '@/utils/formatPersonName';

type View = 'occurred' | 'confirmation';

interface StatCard {
  name: string;
  count: number;
  icon: IconDefinition;
  iconBg: string;
  people: AppointmentAttendance[];
}

interface Props {
  attendances: AppointmentAttendance[];
  meetingStatus?: string | null;
  activeCard: string | null;
  onActiveCardChange: (card: string | null) => void;
  onFilterChange: (ids: Set<string> | null) => void;
}

const AppointmentSummary = ({
  attendances,
  meetingStatus,
  activeCard,
  onActiveCardChange,
  onFilterChange
}: Props) => {
  const isOccurred = meetingStatus === 'occurred';
  const [view, setView] = useState<View>(isOccurred ? 'occurred' : 'confirmation');

  const handleViewChange = (next: View) => {
    setView(next);
    onActiveCardChange(null);
    onFilterChange(null);
  };

  const handleCardClick = (card: StatCard) => {
    if (activeCard === card.name) {
      onActiveCardChange(null);
      onFilterChange(null);
    } else {
      onActiveCardChange(card.name);
      onFilterChange(new Set(card.people.map((a) => a.id)));
    }
  };

  // Occurred buckets
  const attended = attendances.filter((a) => a.internal_status === 'attended');
  const noShows = attendances.filter((a) => a.internal_status === 'no_show');
  const cancelled = attendances.filter((a) =>
    ['cancelled', 'auto_cancelled', 'declined'].includes(a.internal_status)
  );

  // Confirmation buckets
  const confirmed = attendances.filter((a) => a.confirmed_by_member_at != null);
  const declined = attendances.filter((a) => a.internal_status === 'declined');
  const unconfirmed = attendances.filter(
    (a) => !a.confirmed_by_member_at && a.internal_status !== 'declined'
  );

  // Subgroup shown in confirmation view when the appointment occurred
  const attendedWithoutConfirming = attended.filter((a) => !a.confirmed_by_member_at);

  const occurredCards: StatCard[] = [
    {
      name: 'Attended',
      count: attended.length,
      icon: faCircleCheck,
      iconBg: 'bg-green-500',
      people: attended
    },
    {
      name: 'No Show',
      count: noShows.length,
      icon: faCircleXmark,
      iconBg: 'bg-red-500',
      people: noShows
    },
    {
      name: 'Cancelled / Declined',
      count: cancelled.length,
      icon: faCalendarXmark,
      iconBg: 'bg-gray-400',
      people: cancelled
    }
  ];

  const confirmationCards: StatCard[] = [
    {
      name: "I'm Going",
      count: confirmed.length,
      icon: faThumbsUp,
      iconBg: 'bg-green-500',
      people: confirmed
    },
    {
      name: 'Unconfirmed',
      count: unconfirmed.length,
      icon: faClock,
      iconBg: 'bg-yellow-400',
      people: unconfirmed
    },
    {
      name: "I'm Not Going",
      count: declined.length,
      icon: faThumbsDown,
      iconBg: 'bg-red-500',
      people: declined
    }
  ];

  const cards = view === 'occurred' ? occurredCards : confirmationCards;

  return (
    <section className="my-4">
      <div className="flex items-center justify-between mb-3">
        <Heading value={3} text="Summary" />
        <span className="isolate inline-flex rounded-md shadow-sm">
          <button
            type="button"
            onClick={() => handleViewChange('occurred')}
            className={`relative inline-flex items-center rounded-l-md px-3 py-1.5 text-xs font-medium ring-1 ring-inset focus:z-10 ${
              view === 'occurred'
                ? 'bg-blue-600 text-white ring-blue-600'
                : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
            }`}>
            Attendance
          </button>
          <button
            type="button"
            onClick={() => handleViewChange('confirmation')}
            className={`relative -ml-px inline-flex items-center rounded-r-md px-3 py-1.5 text-xs font-medium ring-1 ring-inset focus:z-10 ${
              view === 'confirmation'
                ? 'bg-blue-600 text-white ring-blue-600'
                : 'bg-white text-gray-700 ring-gray-300 hover:bg-gray-50'
            }`}>
            Confirmation
          </button>
        </span>
      </div>

      <dl className="grid grid-cols-3 gap-2 sm:gap-4">
        {cards.map((card) => (
          <MetricCard
            key={card.name}
            metricName={card.name}
            metricValue={card.count}
            icon={
              <FontAwesomeIcon icon={card.icon} className="h-3 w-3 text-white" aria-hidden="true" />
            }
            iconBg={card.iconBg}
            isActive={activeCard === card.name}
            onClick={() => handleCardClick(card)}>
            <PeopleAvatarList people={card.people} />
          </MetricCard>
        ))}
      </dl>

      {view === 'confirmation' && isOccurred && attendedWithoutConfirming.length > 0 && (
        <div className="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-yellow-500 text-sm" />
            <p className="text-sm font-medium text-yellow-800">
              Attended without confirming ({attendedWithoutConfirming.length})
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {attendedWithoutConfirming.map((a) => {
              const name = a.person ? formatPersonName({ person: a.person }) : 'Unknown';
              const avatarName = a.person
                ? formatPersonName({ person: a.person, withPronouns: false })
                : 'Unknown';
              return (
                <span
                  key={a.id}
                  className="mp-sensitive inline-flex items-center gap-1.5 rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                  <Avatar name={avatarName} avatarUrl={a.person?.profile_image_url} size="small" />
                  {name}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default AppointmentSummary;
